import { Injectable, inject } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { MatSnackBar } from '@angular/material/snack-bar';
import { Router } from '@angular/router';
import { AgreementsRoutingConstants } from '@modules/agreements/constants/agreementsRouting.constants';
import { ConditionsRoutingConstants } from '@modules/conditions/constants/conditionsRouting.constants';
import { errorsUri } from '@modules/errors/errors.const';
import { ErrorsService } from '@modules/errors/services/errors.service';
import { ArchivedOrderModalComponent } from '@modules/order/modals/archived-order-modal/archived-order-modal.component';
import { ProposalRoutingConstants } from '@modules/order/order.const';
import { ProposalStatus } from '@modules/order/order.enum';
import { RemoveProposalAlertComponent } from '@modules/order/pages/removeProposalAlertComponent/removeProposalAlert.component';
import { ProposalService } from '@modules/order/services/proposal.service';
import {
  CreateProposal,
  CreateProposalAfterSearchByOrderNumber,
  DeleteProposal,
  FindProposalAfterRedirect,
  GetCartInfo,
  GetPartnerRedirectUrl,
  GetProposalDetails,
  GetProposalIdByOrderNum,
  GetProposalList,
  ProposalTypes,
  SendProposalInBank,
  SendReasonDeleteProposal,
  SetProposalDetails,
  SetProposalList,
  StopLoadingProposalDetails,
  StopLoadingProposalsList,
} from '@modules/order/store/proposal/proposal.action';
import { ModalInformationMessageComponent } from '@modules/shared/components/modalInformationMessage/modalInformationMessage.component';
import { ModalReasonComponent } from '@modules/shared/components/modalReason/modalReason.component';
import { PlatformService } from '@modules/shared/services/platform.service';
import { AppState } from '@modules/shared/store';
import {
  HideSpinnerLoading,
  ShowSpinnerLoading,
} from '@modules/shared/store/common.actions';
import { StatusRoutingConstants } from '@modules/status/constants/statusRouting.constants';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { CommonResources } from '@resources/ru/commonModule/common.resources';
import { ProposalDetails } from '@resources/ru/orderModule/proposal/proposalDetails/proposalDetails.resources';
import { EMPTY, from, interval, of, timer } from 'rxjs';
import {
  concatMap,
  map,
  switchMap,
  take,
  takeWhile,
  tap,
  filter,
  takeUntil,
  catchError,
} from 'rxjs/operators';
import {
  Application,
  CreateApplicationResponse,
  DefaultApplicationResponse,
  GetOrderResponse,
} from '@codegen';
import { DefaultService } from '@codegen/service';
import { HttpErrorResponse } from '@angular/common/http';
import { MainRoutes } from 'src/app/config/routes.config';

@Injectable()
export class ProposalEffects {
  private actions$ = inject(Actions);
  private store = inject<Store<AppState>>(Store);
  private defaultService = inject(DefaultService);
  private router = inject(Router);
  private snackBar = inject(MatSnackBar);
  private proposalService = inject(ProposalService);
  private errors = inject(ErrorsService);
  private dialog = inject(MatDialog);
  private platformService = inject(PlatformService);

  public GetApplicationInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetProposalList>(ProposalTypes.GetProposalList),
      map(data => data.payload),
      switchMap(() =>
        this.defaultService.filter({}).pipe(
          switchMap(res => {
            if (!res.applications) {
              this.store.dispatch(new SetProposalList([]));
            } else {
              this.store.dispatch(new SetProposalList(res.applications));
            }
            this.store.dispatch(new HideSpinnerLoading());
            return [res];
          })
        )
      ),
      switchMap(res => {
        this.checkProposalsStatus(res.applications);
        const stopLoading$ = this.actions$.pipe(
          ofType(ProposalTypes.StopLoadingProposalsList)
        );
        return interval(this.getStatusInterval).pipe(
          takeUntil(stopLoading$),
          switchMap(() => {
            return this.defaultService.filter({});
          }),
          concatMap((data): Action[] => {
            if (!data.applications) {
              this.store.dispatch(new SetProposalList([]));
            } else {
              this.checkProposalsStatus(data.applications);
            }
            return [];
          })
        );
      })
    )
  );

  public GetApplicationById$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetProposalDetails>(ProposalTypes.GetProposalDetails),
      map(data => data.payload),
      switchMap(applicationId =>
        this.defaultService.getById({ applicationId }).pipe(
          switchMap(res => {
            this.store.dispatch(new SetProposalDetails(res.application));
            this.store
              .select(state => state.common.isHideSpinnerLoading)
              .pipe(
                filter(isHideSpinnerLoading => isHideSpinnerLoading),
                tap(() => this.store.dispatch(new HideSpinnerLoading()))
              )
              .subscribe();
            return of(res.application);
          })
        )
      ),
      switchMap(proposal => {
        return interval(this.getStatusInterval).pipe(
          takeWhile(
            () =>
              !this.isStopLoadingProposalDetails &&
              this.proposalService.updateProposalInfoByStatus(
                proposal.statusCode as ProposalStatus
              )
          ),
          switchMap(() =>
            this.defaultService.getById({ applicationId: proposal.id })
          ),
          switchMap(response => {
            return [
              new SetProposalDetails(response.application),
              new HideSpinnerLoading(),
            ];
          })
        );
      })
    )
  );

  public CreateApplication$ = createEffect(() =>
    this.actions$.pipe(
      ofType<CreateProposal>(ProposalTypes.CreateProposal),
      switchMap(() => {
        return this.defaultService
          .create({ orderNumber: localStorage.getItem('order') })
          .pipe(
            switchMap(response => {
              if (response.errorInfo.error === '10003') {
                if (response?.applicationId) {
                  localStorage.removeItem('order');
                  this.router.navigateByUrl(
                    `/order/${ProposalRoutingConstants.proposalsDetails}/${response.applicationId}`
                  );
                } else {
                  this.router.navigateByUrl(`/${errorsUri.proposalCreate}`, {
                    state: {
                      actionId: response.actionId,
                      applicationId: response.applicationId,
                    },
                  });
                  localStorage.removeItem('order');
                }
                return [new HideSpinnerLoading()];
              } else {
                localStorage.removeItem('order');
                this.router.navigate([
                  `${'/agreements/' + AgreementsRoutingConstants.proposalAgreement}`,
                  response.applicationId,
                ]);
                return EMPTY;
              }
            })
          );
      })
    )
  );

  public CreateProposalAfterSearchByOrderNumber$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType<CreateProposalAfterSearchByOrderNumber>(
          ProposalTypes.CreateProposalAfterSearchByOrderNumber
        ),
        map(data => data.payload),
        switchMap((proposalNumber: string) =>
          this.defaultService.create({
            orderNumber: proposalNumber || localStorage.getItem('order'),
          })
        ),
        map((response: CreateApplicationResponse) => {
          if (response.errorInfo.error === '10003') {
            if (response?.applicationId) {
              this.router.navigateByUrl(
                `/order/${ProposalRoutingConstants.proposalsDetails}/${response.applicationId}`
              );
            } else {
              this.router.navigateByUrl(`/${errorsUri.proposalCreate}`, {
                state: {
                  actionId: response?.actionId,
                  applicationId: response?.applicationId,
                },
              });
            }
          } else {
            this.router.navigateByUrl(
              `/agreements/${AgreementsRoutingConstants.proposalAgreement}/${response.applicationId}`
            );
          }
        })
      ),
    {
      dispatch: false,
    }
  );

  public FindProposalAfterRedirect$ = createEffect(() =>
    this.actions$.pipe(
      ofType<FindProposalAfterRedirect>(
        ProposalTypes.FindProposalAfterRedirect
      ),
      map(data => data.payload),
      tap(() => this.store.dispatch(new HideSpinnerLoading())),
      switchMap((orderNumber: string) =>
        this.defaultService.cartSearchUnattachedCart({
          orderNumber: orderNumber || localStorage.getItem('order'),
        })
      ),
      switchMap((data: GetOrderResponse) => {
        this.store.dispatch(new HideSpinnerLoading());

        if (data.errorInfo.error === '10004') {
          this.store.dispatch(new ShowSpinnerLoading('Мы ищем ваш заказ'));
          return [data];
        } else if (data.errorInfo.error === '10005') {
          if (data?.applicationId) {
            localStorage.removeItem('order');
            this.router.navigateByUrl(
              `/order/${ProposalRoutingConstants.proposalsDetails}/${data.applicationId}`
            );
          } else {
            localStorage.removeItem('order');
            this.router.navigateByUrl(`/${errorsUri.proposalCreated}`, {
              state: {
                actionId: data?.actionId,
                applicationId: data?.applicationId,
              },
            });
          }
          return EMPTY;
        } else if (data.errorInfo.error === '10006') {
          localStorage.removeItem('order');
          this.router.navigateByUrl(`/${errorsUri.proposalRejected}`, {
            state: {
              actionId: data?.actionId,
              applicationId: data?.applicationId,
            },
          });
          return EMPTY;
        } else {
          this.store.dispatch(new CreateProposal());
          return EMPTY;
        }
      }),
      switchMap(() => {
        const int = 5000;

        return interval(int).pipe(
          take(1),
          switchMap(() =>
            this.defaultService.cartSearchUnattachedCart({
              orderNumber: localStorage.getItem('order'),
            })
          ),
          switchMap((data: GetOrderResponse) => {
            this.store.dispatch(new HideSpinnerLoading());

            if (data.errorInfo.error === '10004') {
              localStorage.removeItem('order');
              this.store.dispatch(new HideSpinnerLoading());
              this.router.navigateByUrl(`/${errorsUri.proposalNotFound}`, {
                state: {
                  actionId: data?.actionId,
                  applicationId: data?.applicationId,
                },
              });
              return EMPTY;
            } else if (data.errorInfo.error === '10006') {
              localStorage.removeItem('order');
              this.router.navigateByUrl(`/${errorsUri.proposalRejected}`, {
                state: {
                  actionId: data?.actionId,
                  applicationId: data?.applicationId,
                },
              });
              return [new HideSpinnerLoading()];
            } else {
              this.store.dispatch(new CreateProposal());
              return EMPTY;
            }
          })
        );
      })
    )
  );

  public SendApplicationInBank$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SendProposalInBank>(ProposalTypes.SendProposalInBank),
      map(data => data.payload),
      switchMap((applicationId: string) => {
        return this.defaultService.offerSend({ applicationId }).pipe(
          switchMap((data: DefaultApplicationResponse) => {
            this.store.dispatch(new ShowSpinnerLoading());

            if (data.errorInfo?.error === '501') {
              this.router.navigateByUrl(`/${errorsUri.unknown}`, {
                state: {
                  actionId: data.actionId,
                  applicationId,
                },
              });

              return EMPTY;
            } else {
              return this.defaultService.getById({
                applicationId,
              });
            }
          }),
          switchMap(response => {
            if (!response?.application) {
              return EMPTY;
            }

            const statusCode = response.application.statusCode;

            switch (statusCode) {
              case ProposalStatus.partnerReceived:
                if (response.application.partnerProductInfo.partnerUrl.length) {
                  document.location.assign(
                    response.application.partnerProductInfo.partnerUrl
                  );
                } else {
                  this.dialog
                    .open(ArchivedOrderModalComponent, {
                      width: this.platformService.dialogWidth,
                      height: '256px',
                      data: { id: null },
                      disableClose: true,
                    })
                    .afterClosed()
                    .pipe(
                      switchMap(() =>
                        from(
                          this.router.navigate([
                            `/order/${ProposalRoutingConstants.proposalsList}`,
                          ])
                        )
                      ),
                      take(1)
                    )
                    .subscribe();
                }
                return [new HideSpinnerLoading()];
              case ProposalStatus.partnerAwaiting:
                return [
                  new GetPartnerRedirectUrl(applicationId),
                  new HideSpinnerLoading(),
                ];
              case ProposalStatus.offersPrepared:
                this.router.navigate([
                  `/conditions/${ConditionsRoutingConstants.proposalConditions}/${applicationId}`,
                ]);
                return [new HideSpinnerLoading()];
              case ProposalStatus.rejected:
                this.router.navigate([
                  `/status/${StatusRoutingConstants.proposalStatus}/${applicationId}`,
                ]);
                return [new HideSpinnerLoading()];
              case ProposalStatus.scoring:
                this.router.navigate([
                  `/status/${StatusRoutingConstants.timer}/${applicationId}`,
                ]);
                return [new HideSpinnerLoading()];
              default:
                return [new HideSpinnerLoading()];
            }
          }),
          catchError((error: HttpErrorResponse) => {
            if (error.status === 501) {
              this.router.navigateByUrl(`/${errorsUri.unknown}`, {
                state: {
                  actionId: '',
                  applicationId: applicationId,
                },
              });
            }

            return of(new HideSpinnerLoading());
          })
        );
      })
    )
  );

  public StopLoadingProposalsList$ = createEffect(() =>
    this.actions$.pipe(
      ofType<StopLoadingProposalsList>(ProposalTypes.StopLoadingProposalsList),
      map(data => data.payload),
      switchMap((isStopLoading): Action[] => {
        this.isStopLoadingProposalsList = isStopLoading;
        return [];
      })
    )
  );

  public StopLoadingProposalDetails$ = createEffect(() =>
    this.actions$.pipe(
      ofType<StopLoadingProposalDetails>(
        ProposalTypes.StopLoadingProposalDetails
      ),
      map(data => data.payload),
      switchMap((isStopLoading): Action[] => {
        this.isStopLoadingProposalDetails = isStopLoading;
        return [];
      })
    )
  );

  public GetPartnerRedirectUrl$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetPartnerRedirectUrl>(ProposalTypes.GetPartnerRedirectUrl),
      map(data => data.payload),
      switchMap(proposalId =>
        this.defaultService
          .offerUrl({
            applicationId: proposalId,
          })
          .pipe(
            switchMap(res => {
              if (res.processAs == 'FRAME') {
                this.router.navigate([MainRoutes.SHOW_IFRAME], {
                  state: {
                    urlForIframe: res.confirmationUrl,
                    applicationId: proposalId,
                  },
                });
              } else {
                document.location.assign(res.confirmationUrl);
              }

              return [new HideSpinnerLoading()];
            })
          )
      )
    )
  );

  public GetProposalIdByOrderNum$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetProposalIdByOrderNum>(ProposalTypes.GetProposalIdByOrderNum),
      map(data => data.payload),
      switchMap(data =>
        this.defaultService.offerReject({
          id: data.id,
          number: data.number,
          reason: data.reason,
        })
      ),
      switchMap((res): Action[] => {
        this.router.navigateByUrl(
          `/order/${ProposalRoutingConstants.proposalArchivalDetails}/${res.applicationId}`
        );
        return [];
      })
    )
  );

  public SendReasonDeleteProposal$ = createEffect(() =>
    this.actions$.pipe(
      ofType<SendReasonDeleteProposal>(ProposalTypes.SendReasonDeleteProposal),
      map(data => data.payload),
      switchMap(data => {
        const { proposalNumber } = data;
        return this.defaultService
          .cancelReason({
            applicationId: data.proposalId,
            rejectedReasonId: data.reasonId,
          })
          .pipe(
            switchMap((): Action[] => {
              this.snackBar.openFromComponent(RemoveProposalAlertComponent, {
                data: {
                  proposalNumber,
                },
                duration: 2000,
              });
              this.router.navigateByUrl(
                `/order/${ProposalRoutingConstants.proposalsList}`
              );
              return [];
            })
          );
      })
    )
  );

  public GetCartInfo$ = createEffect(() =>
    this.actions$.pipe(
      ofType<GetCartInfo>(ProposalTypes.GetCartInfo),
      map(data => data.payload),
      switchMap((proposalNumber: string) =>
        this.defaultService
          .cartSearchUnattachedCart({
            orderNumber: proposalNumber || localStorage.getItem('order'),
          })
          .pipe(
            switchMap(data => {
              if (data.errorInfo.error === '10005') {
                this.dialog.closeAll();
                if (data?.applicationId) {
                  this.router.navigateByUrl(
                    `/order/${ProposalRoutingConstants.proposalsDetails}/${data.applicationId}`
                  );
                } else {
                  this.errors.showError(data, '', {
                    actionId: data.actionId || '',
                    applicationId: data.applicationId || '',
                  });
                }
                return [new HideSpinnerLoading()];
              } else if (data.errorInfo.error === '10004') {
                this.dialog.closeAll();
                localStorage.removeItem('order');
                this.errors.showError(data, '', {
                  actionId: data.actionId || '',
                  applicationId: data.applicationId || '',
                });
                return [new HideSpinnerLoading()];
              } else if (data.errorInfo.error === '10006') {
                this.dialog.closeAll();
                this.errors.showError(data, '', {
                  actionId: data.actionId || '',
                  applicationId: data.applicationId || '',
                });
                return [new HideSpinnerLoading()];
              }
              this.router.navigateByUrl(
                `/order/${ProposalRoutingConstants.confirmProposal}/${proposalNumber}`
              );
              this.dialog.closeAll();
              return [new SetProposalDetails(data), new HideSpinnerLoading()];
            })
          )
      )
    )
  );

  public DeleteProposal$ = createEffect(() =>
    this.actions$.pipe(
      ofType<DeleteProposal>(ProposalTypes.DeleteProposal),
      map(data => data.payload),
      switchMap(applicationId =>
        this.defaultService
          .cancel({
            applicationId,
          })
          .pipe(
            switchMap(() => {
              return this.proposalService.getReasonCancelProposal().pipe(
                switchMap(reasons => {
                  const dialogOptions = {
                    width: this.platformService.dialogWidth,
                    minWidth: '596px',
                    height: '549px',
                  };

                  const dialogRef = this.dialog.open(ModalReasonComponent, {
                    ...dialogOptions,
                    data: {
                      reasons,
                      title: this.proposalDetailsDictionary.reasonForDeleting,
                    },
                  });

                  return dialogRef.afterClosed().pipe(
                    tap(reason => {
                      if (!reason) {
                        this.router.navigate([
                          `/order/${ProposalRoutingConstants.proposalsList}`,
                        ]);
                        window.scrollTo({ top: 0, left: 0 });
                      }
                    }),
                    filter(reason => reason !== undefined),
                    switchMap(reasons => {
                      const comment =
                        reasons.comment !== undefined
                          ? reasons.comment
                          : undefined;

                      return this.defaultService.cancelReason({
                        applicationId: applicationId,
                        rejectedReasonIdList: reasons.rejectedReasonIdList,
                        comment: comment,
                      });
                    }),
                    switchMap(() => {
                      const dialogRef = this.dialog.open(
                        ModalInformationMessageComponent,
                        {
                          autoFocus: false,
                          data: {
                            text: this.commonResources.thanksMessage,
                            icon: '/assets/heart.svg',
                          },
                        }
                      );

                      return timer(this.closeTimeout).pipe(
                        tap(() => dialogRef.close()),
                        switchMap((): Action[] => {
                          this.router.navigate([
                            `/order/${ProposalRoutingConstants.proposalsList}`,
                          ]);
                          window.scrollTo({ top: 0, left: 0 });
                          return [];
                        })
                      );
                    })
                  );
                })
              );
            })
          )
      )
    )
  );

  public proposalDetailsDictionary: ProposalDetails = new ProposalDetails();
  public commonResources: CommonResources = new CommonResources();

  private getStatusInterval: number = 5_000;
  private isStopLoadingProposalsList: boolean = false;
  private isStopLoadingProposalDetails: boolean = false;
  private closeTimeout = 3000;

  private checkProposalsStatus(proposalsList: Application[]): void {
    this.isStopLoadingProposalsList = proposalsList
      ? proposalsList.some(
          proposal =>
            proposal.statusCode === ProposalStatus.scoring ||
            proposal.statusCode === ProposalStatus.underwriting ||
            proposal.statusCode === ProposalStatus.partnerReceived ||
            proposal.statusCode === ProposalStatus.partnerPending ||
            proposal.statusCode === ProposalStatus.partnerApproved ||
            proposal.statusCode === ProposalStatus.partnerSigned ||
            proposal.statusCode === ProposalStatus.partnerRejected ||
            proposal.statusCode === ProposalStatus.canceled
        )
      : true;
  }
}
